import { useEffect, useState } from "react";
import { FileText, FileSpreadsheet, Download, CalendarRange, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, isAdmin, isSuperadmin, isTechnician } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, LineChart, Line, Legend,
} from "recharts";
import { exportReportPDF, exportReportExcel, exportReportCSV } from "@/lib/reportExport";

const PALETTE = ["#0047FF", "#22D3EE", "#7C3AED", "#16A34A", "#F59E0B", "#EC4899", "#06B6D4", "#64748B"];
const tip = { background: "rgba(255,255,255,0.95)", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 12, fontSize: 12 };

function iso(d) { return d.toISOString().slice(0, 10); }
function presetRange(preset) {
  const to = new Date();
  const from = new Date();
  if (preset === "week") from.setDate(to.getDate() - 7);
  else if (preset === "month") from.setMonth(to.getMonth() - 1);
  else if (preset === "quarter") from.setMonth(to.getMonth() - 3);
  return { from: iso(from), to: iso(to) };
}

export default function Reports() {
  const { user } = useAuth();
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (isAdmin(user)) {
      api.users({ role: "team", limit: 200 }).then((r) => setTechs(r.users || r || [])).catch(() => {});
    }
  }, [user]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p !== "custom") setRange(presetRange(p));
  };

  const run = async () => {
    setLoading(true); setData(null);
    try {
      const params = { from: range.from, to: range.to };
      if (isAdmin(user) && techId) params.technician_id = techId;
      const res = await api.report(params);
      setData(res);
    } catch (e) { toast.error(e.message || "Could not build report"); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  const downloadPDF = async () => {
    if (!data) return;
    setPdfBusy(true);
    try { await exportReportPDF(data); toast.success("PDF downloaded"); }
    catch (e) { toast.error("PDF failed: " + e.message); }
    finally { setPdfBusy(false); }
  };

  const k = data?.kpis || {};
  const statusData = (data?.byStatus || []).map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-cobalt-500">Reports</div>
        <h1 className="font-display text-3xl font-black tracking-tighter">
          {isTechnician(user) ? "My performance report" : "Service desk report"}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Pick a period, review the summary, and download a branded PDF or Excel export.
        </p>
      </div>

      {/* controls */}
      <div className="glass rounded-2xl p-5 flex flex-wrap items-end gap-4">
        <div>
          <div className="overline mb-1.5">Period</div>
          <div className="flex gap-1.5">
            {[["week", "Weekly"], ["month", "Monthly"], ["quarter", "Quarter"], ["custom", "Custom"]].map(([p, l]) => (
              <button key={p} onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${preset === p ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "glass"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <div className="flex items-end gap-2">
            <div>
              <div className="overline mb-1.5">From</div>
              <input type="date" value={range.from} max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="rounded-xl border px-3 py-2 text-sm bg-transparent" />
            </div>
            <div>
              <div className="overline mb-1.5">To</div>
              <input type="date" value={range.to} min={range.from} max={iso(new Date())}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="rounded-xl border px-3 py-2 text-sm bg-transparent" />
            </div>
          </div>
        )}

        {isAdmin(user) && (
          <div>
            <div className="overline mb-1.5">Technician</div>
            <Select value={techId || "all"} onValueChange={(v) => setTechId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-52 rounded-xl"><SelectValue placeholder="All technicians" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All technicians</SelectItem>
                {techs.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button onClick={run} disabled={loading} className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarRange className="h-4 w-4" />}
          Generate
        </Button>

        <div className="ml-auto flex gap-2">
          <Button onClick={downloadPDF} disabled={!data || pdfBusy} variant="outline" className="rounded-full gap-1.5">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </Button>
          <Button onClick={() => data && exportReportExcel(data)} disabled={!data} variant="outline" className="rounded-full gap-1.5">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </Button>
          <Button onClick={() => data && exportReportCSV(data)} disabled={!data} variant="outline" className="rounded-full gap-1.5">
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {loading && <div className="text-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Building report…</div>}

      {data && !loading && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Total tickets", k.total ?? 0],
              ["SLA compliance", (k.slaCompliance ?? 0) + "%"],
              ["Avg resolution", k.avgResolveHrs != null ? k.avgResolveHrs + "h" : "—"],
              ["Resolution rate", (k.resolutionRate ?? 0) + "%"],
            ].map(([l, v]) => (
              <div key={l} className="glass rounded-2xl p-5">
                <div className="font-display text-3xl font-extrabold tracking-tighter">{v}</div>
                <div className="overline mt-1">{l}</div>
              </div>
            ))}
          </div>

          {/* charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-5">
              <div className="overline mb-2">Tickets by status</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {statusData.map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <Tooltip contentStyle={tip} /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="overline mb-2">Tickets by category</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.byCategory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tip} />
                    <Bar dataKey="value" fill="#0047FF" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="overline mb-2">Raised vs Resolved</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tip} /><Legend />
                    <Line type="monotone" dataKey="raised" name="Raised" stroke="#0047FF" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#16A34A" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="overline mb-2">Technician performance</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.byTechnician} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tip} /><Legend />
                    <Bar dataKey="total" name="Assigned" fill="#94a3b8" radius={[0, 5, 5, 0]} />
                    <Bar dataKey="resolved" name="Resolved" fill="#16A34A" radius={[0, 5, 5, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ticket table */}
          <div className="glass rounded-2xl p-5">
            <div className="overline mb-3">Ticket details · {data.tickets?.length || 0} tickets</div>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-sm">
                <thead className="text-left sticky top-0 bg-white/80 dark:bg-zinc-900/80 backdrop-blur">
                  <tr className="overline">
                    <th className="py-2 pr-3">Ticket</th><th className="pr-3">Title</th><th className="pr-3">Category</th>
                    <th className="pr-3">Priority</th><th className="pr-3">Status</th><th className="pr-3">Assignee</th><th className="pr-3">Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.tickets || []).slice(0, 200).map((t) => (
                    <tr key={t.ticket_no} className="border-t border-black/5">
                      <td className="py-2 pr-3 text-mono text-xs">{t.ticket_no}</td>
                      <td className="pr-3">{t.title}</td>
                      <td className="pr-3">{t.category}</td>
                      <td className="pr-3 capitalize">{t.priority}</td>
                      <td className="pr-3 capitalize">{t.status}</td>
                      <td className="pr-3">{t.assignee}</td>
                      <td className="pr-3 text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data.tickets?.length || 0) > 200 && (
                <div className="text-center text-xs text-muted-foreground py-3">Showing first 200 — full list in the PDF/Excel export.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
